
import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  signIn: (emailOrPhone: string, password: string) => Promise<{ error: any }>;
  signUp: (emailOrPhone: string, password: string, isPhone?: boolean) => Promise<{ error: any }>;
  signInWithPhone: (phone: string, password: string) => Promise<{ error: any }>;
  signUpWithPhone: (phone: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    let mounted = true;

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return;
        
        console.log('Auth state changed:', event, session?.user?.email);
        setSession(session);
        setUser(session?.user ?? null);
        
        // Check admin status when user logs in
        if (session?.user) {
          setTimeout(async () => {
            if (!mounted) return;
            try {
              const { data, error } = await supabase
                .from('profiles')
                .select('role')
                .eq('id', session.user.id)
                .single();

              if (!error && data && mounted) {
                setIsAdmin(data.role === 'admin');
              }
            } catch (error) {
              console.error('Error checking admin status:', error);
            }
          }, 0);
        } else {
          if (mounted) {
            setIsAdmin(false);
          }
        }
        
        if (mounted) {
          setLoading(false);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (mounted) {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (emailOrPhone: string, password: string) => {
    try {
      console.log('Attempting sign in for:', emailOrPhone);
      
      // Determine if input is phone or email
      const isPhone = /^\+?[\d\s\-\(\)]+$/.test(emailOrPhone.trim());
      
      const { error } = await supabase.auth.signInWithPassword({
        ...(isPhone ? { phone: emailOrPhone.trim() } : { email: emailOrPhone.trim().toLowerCase() }),
        password,
      });

      if (error) {
        console.error('Sign in error:', error);
        let errorMessage = 'Sign in failed. Please try again.';
        
        if (error.message.includes('Invalid login credentials')) {
          errorMessage = isPhone ? 'Invalid phone number or password. Please check your credentials.' : 'Invalid email or password. Please check your credentials.';
        } else if (error.message.includes('Email not confirmed')) {
          errorMessage = 'Please confirm your email address before signing in.';
        } else if (error.message.includes('Phone not confirmed')) {
          errorMessage = 'Please confirm your phone number before signing in.';
        } else if (error.message.includes('Too many requests')) {
          errorMessage = 'Too many sign-in attempts. Please wait before trying again.';
        }
        
        toast({
          title: "Sign In Error",
          description: errorMessage,
          variant: "destructive",
        });
      } else {
        console.log('Sign in successful');
        toast({
          title: "Welcome back!",
          description: "You have been signed in successfully.",
        });
      }

      return { error };
    } catch (error: any) {
      console.error('Unexpected sign in error:', error);
      toast({
        title: "Sign In Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
      return { error };
    }
  };

  const signInWithPhone = async (phone: string, password: string) => {
    return signIn(phone, password);
  };

  const signUp = async (emailOrPhone: string, password: string, isPhone: boolean = false) => {
    try {
      console.log('Starting signup process for:', emailOrPhone, 'isPhone:', isPhone);
      
      // Auto-detect if not explicitly specified
      if (!isPhone) {
        isPhone = /^\+?[\d\s\-\(\)]+$/.test(emailOrPhone.trim());
      }
      
      const { error } = isPhone 
        ? await supabase.auth.signUp({
            phone: emailOrPhone.trim(),
            password,
            options: {
              channel: 'sms' as const
            }
          })
        : await supabase.auth.signUp({
            email: emailOrPhone.trim().toLowerCase(),
            password,
            options: {
              emailRedirectTo: `${window.location.origin}/`
            }
          });

      if (error) {
        console.error('Sign up error:', error);
        let errorMessage = 'Sign up failed. Please try again.';
        
        if (error.message.includes('User already registered')) {
          errorMessage = `An account with this ${isPhone ? 'phone number' : 'email'} already exists. Please sign in instead.`;
        } else if (error.message.includes('Password should be at least')) {
          errorMessage = 'Password must be at least 6 characters long.';
        } else if (error.message.includes('Invalid email')) {
          errorMessage = 'Please enter a valid email address.';
        } else if (error.message.includes('Invalid phone')) {
          errorMessage = 'Please enter a valid phone number.';
        } else if (error.message.includes('signup is disabled')) {
          errorMessage = 'New user registration is currently disabled. Please contact support.';
        } else if (error.message.includes('captcha verification')) {
          errorMessage = 'Captcha verification is required but currently unavailable. Please try again later or contact support.';
        }
        
        toast({
          title: "Sign Up Error",
          description: errorMessage,
          variant: "destructive",
        });
      } else {
        console.log('Signup successful');
        toast({
          title: "Account Created Successfully!",
          description: isPhone 
            ? "Please check your phone for an SMS with the verification code."
            : "Please check your email to confirm your account, then you can sign in.",
        });
      }

      return { error };
    } catch (error: any) {
      console.error('Unexpected sign up error:', error);
      toast({
        title: "Sign Up Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
      return { error };
    }
  };

  const signUpWithPhone = async (phone: string, password: string) => {
    return signUp(phone, password, true);
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
      setSession(null);
      setIsAdmin(false);
      toast({
        title: "Signed Out",
        description: "You have been signed out successfully.",
      });
    } catch (error: any) {
      console.error('Sign out error:', error);
      toast({
        title: "Sign Out Error",
        description: "Failed to sign out. Please try again.",
        variant: "destructive",
      });
    }
  };

  const value = {
    user,
    session,
    loading,
    isAdmin,
    signIn,
    signUp,
    signInWithPhone,
    signUpWithPhone,
    signOut,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
